import { InjectionToken } from '@angular/core';
import { IndividualConfig } from 'ngx-toastr';

import { SnackbarTemplateType } from '@ninety/platform/models/enums/snackbar-template-type';
import { NotifyV2Params } from '@ninety/platform/models/notifications/notify-v2-params';

/**
 * Structural slice of `NotifyService`'s public surface needed by `NotificationStateEffects`
 * (DEV-26105 T5). The concrete `NotifyService` lives in `shared/ui/core`; importing it here would
 * reopen the `platform/state -> shared/ui/core` edge. The effect injects this token and the app
 * composition root binds it via `{ provide: NOTIFY_CONTROL, useExisting: NotifyService }`.
 */
export interface NotifyControl {
  notify(message: string, duration?: number, action?: string): void;
  notifyV2(message: string, params?: NotifyV2Params): void;
  showError(message: string, title?: string, override?: Partial<IndividualConfig>): void;
  success(message?: string, title?: string, override?: Partial<IndividualConfig>): void;
  notifyWithTemplate(templateType: SnackbarTemplateType, data: unknown, duration?: number): void;
  toast(message?: string, title?: string, override?: Partial<IndividualConfig>, type?: string): void;
}

/**
 * Defaults to a no-op so the effect never hits a null injector in test/storybook contexts. The app
 * composition root overrides it with the real `NotifyService`.
 */
export const NOTIFY_CONTROL = new InjectionToken<NotifyControl>('NOTIFY_CONTROL', {
  providedIn: 'root',
  factory: (): NotifyControl => ({
    notify: () => undefined,
    notifyV2: () => undefined,
    showError: () => undefined,
    success: () => undefined,
    notifyWithTemplate: () => undefined,
    toast: () => undefined,
  }),
});
